import React, { useState } from 'react';

import { useBreadcrumbContext } from 'components/shared/Breadcrumb/context/BreadcrumbContext';
import Button from 'components/shared/Button/Button';

import { useSubModal } from '../../lib/SubModalContext';

const SubModalFormContent = ({
  ContentComponent,
  submitText,
  onSubmit,
  disableSubmit,
  onClose
}) => {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);

  const setValue = (key, value) => {
    setValues((old) => ({ ...old, [key]: value }));
  };

  const isDisabled =
    typeof disableSubmit === 'function' ? !!disableSubmit(values) : !!disableSubmit;

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const result = await onSubmit?.(values);
      if (result !== false) {
        onClose();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto">
        <ContentComponent setValue={setValue} values={values} close={onClose} />
      </div>
      <div className="flex h-[56px] shrink-0 items-center justify-end gap-2 border-0 border-t border-solid border-neutral-100 bg-white px-4 py-3">
        <Button text="Cancel" size="small" style="outlined" color="neutral" onClick={onClose} />
        <Button
          text={submitText || 'Submit'}
          size="small"
          onClick={handleSubmit}
          isLoading={loading}
          isDisabled={isDisabled}
        />
      </div>
    </div>
  );
};

const ModalFooter = ({
  tab,
  setDetails,
  selectedData,
  resetSelections = () => {},
  lockNext,
  setModal,
  initialStep,
  handleClose = () => {},
  contentValues
}) => {
  const { step, steps, goNext, goBack } = useBreadcrumbContext();
  const { openSubModal, closeSubModal } = useSubModal();

  const openModal = (modalProps) => {
    setModal(modalProps);

    const handleModalClose = () => {
      setModal(null);
      closeSubModal();
    };

    const hasSubmit = !!modalProps?.onSubmit;

    openSubModal({
      title: modalProps?.title,
      className: hasSubmit ? '!overflow-hidden !p-0' : modalProps?.contentClassName,
      panelClassName: modalProps?.height ? `h-[${modalProps.height}]` : '',
      footer: hasSubmit ? false : undefined,
      content: modalProps?.content ? (
        hasSubmit ? (
          <SubModalFormContent
            ContentComponent={modalProps.content}
            submitText={modalProps?.submitText}
            onSubmit={modalProps?.onSubmit}
            disableSubmit={modalProps?.disabled || modalProps?.disableSubmit}
            onClose={handleModalClose}
          />
        ) : (
          <modalProps.content close={handleModalClose} />
        )
      ) : null,
      actions: { onClose: handleModalClose }
    });
  };

  const isLastStep = step === steps?.length - 1;
  const [loading, setLoading] = useState(false);

  const handleContinue = async () => {
    if (loading) return;
    setLoading(true);
    try {
      if (tab?.onContinue) {
        await tab.onContinue(selectedData, goNext, contentValues);
      } else if (isLastStep) {
        await handleClose();
      } else {
        goNext();
      }
    } finally {
      setLoading(false);
      setDetails(null);
    }
  };

  return (
    <div className="flex w-full items-center justify-between gap-2 bg-white">
      {tab?.leftFooter ? (
        <tab.leftFooter resetSelections={resetSelections} openModal={openModal} />
      ) : (
        <div></div>
      )}
      <div className="flex gap-2">
        {!tab?.hideBackButton && (
          <Button
            text={step === initialStep ? 'Close' : 'Back'}
            size="small"
            style="outlined"
            color="neutral"
            onClick={step === initialStep ? handleClose : () => goBack(true)}
          />
        )}
        {typeof tab?.continueText === 'function' ? (
          tab?.continueText({ tab, step, steps, goBack, goNext, lockNext, loading, handleContinue })
        ) : (
          <Button
            text={tab?.continueText ? tab?.continueText : isLastStep ? 'Done' : 'Continue'}
            isDisabled={lockNext || tab?.continueLoading}
            isLoading={loading || tab?.continueLoading}
            onClick={handleContinue}
            size="small"
          />
        )}
      </div>
    </div>
  );
};

export default ModalFooter;
